<template>
  <page-wrapper ref="pageEl" title="Subscriber Accounting Report Page">
    <div class="page-container">
      <div class="row mb-7" style="padding: 15px">
        <div class="col-4">
          <div class="label">วันที่เอกสาร</div>
          <date-picker
            v-model="dateRange"
            :disabled="!isDropdownDisabled"
            type="daterange"
            style="width: 100%"
          ></date-picker>
        </div>
        <div class="col-4">
          <div class="label">สถานะเอกสาร</div>
          <el-select
            v-model="query.status"
            :disabled="!isDropdownDisabled"
            clearable
            style="width: 100%"
          >
            <el-option
              v-for="t in actionBillingFilterMapping"
              :key="t.value"
              :label="t.label"
              :value="t.value"
            ></el-option>
          </el-select>
        </div>
        <div class="col-4">
          <div class="label">ประเภทเอกสาร</div>
          <el-select
            v-model="query.receiptType"
            :disabled="!isDropdownDisabled"
            style="width: 100%"
            clearable
          >
            <el-option
              v-for="t in filterReceiptType"
              :key="t.value"
              :label="t.label"
              :value="t.value"
            ></el-option>
          </el-select>
        </div>
        <div class="mt-3 col-4">
          <div class="label">เลขที่เอกสาร</div>
          <el-input
            v-model="query.documentNumber"
            :disabled="!isSearchDisabled"
            class="text-document"
            style="width: 100%"
          ></el-input>
        </div>
      </div>
      <div class="text-end mt-2">
        <sc-button
          :disabled="!isClearDisabled"
          variant="warning"
          @click="handleClickClear"
          >clear</sc-button
        >
      </div>
      <div class="text-end mt-2">
        <sc-button
          :disabled="!isSearchDisabled"
          variant="primary"
          @click="handleClickSearch"
          >ค้นหา</sc-button
        >
      </div>
      <div>
        <button
          :disabled="!isClickDownloadAsCSVDisabled"
          variant="secondary"
          @click.prevent="handleClickDownloadAsCSV"
        >
          Download
        </button>
      </div>
      <div class="report-container">
        <div class="row mt-3">
          <div class="col-6">{{ totalCount }}</div>
          <div class="col-6 top-page">
            <el-pagination
              :total="total"
              :disabled="!isPaginationDisable"
              :current-page="query.page"
              :page-size="30"
              background
              layout="prev, pager, next"
              @current-change="handleCurrentChange"
            ></el-pagination>
          </div>
        </div>
        <div class="report-table">
          <div
            v-for="(report, index) in billingReportPresenters"
            :key="index"
            :class="{ header: index === 0 }"
            class="report-billing-row"
          >
            <div class="report-cell text-center">{{ report.signed_at }}</div>
            <div class="report-cell text-center">
              <div v-if="index === 0">{{ report.document_number }}</div>
              <router-link
                v-else-if="report.document_number && index !== 0"
                :to="{
                  name: 'ReceiptDetailPage',
                  params: { id: report.id },
                }"
                target="_blank"
                >{{ report.document_number }}</router-link
              >
            </div>
            <div
              class="report-cell name"
              style="white-space: pre-wrap"
            >
              {{ report.billing_address }}
            </div>
            <div class="report-cell text-center">
              {{ report.mobile_number }}
            </div>
            <div class="report-cell text-center">
              <div v-if="index === 0">{{ report.branch_number }}</div>
              <input
                v-else-if="!!report.branch_number"
                v-model="report.branch_number"
                :disabled="!!report.branch_number"
                type="checkbox"
                value="owner"
              />
            </div>
            <div class="report-cell text-center">
              <div v-if="index === 0">{{ report.action }}</div>
              <div v-else-if="!!report.action">
                <div>
                  <a
                    class="edit"
                    href="#"
                    @click="handleShowImageEditer(report)"
                  >
                    {{ report.albunText }}
                  </a>
                </div>
                <div
                  :class="report.action"
                  class="badge"
                  @click="handleSave(report.id, 'action', report.action)"
                >
                  {{ report.action_th || report.action }}
                </div>
              </div>
            </div>
            <div class="report-cell text-center">
              <div v-if="index === 0">{{ report.action_status }}</div>
              <div v-else :class="report.action" class="action">
                {{ report.action_status }}
              </div>
            </div>
            <div class="report-cell">
              <div v-if="index === 0">{{ report.note }}</div>
              <el-input
                v-else
                :value="report.note"
                type="textarea"
                rows="3"
                @input="handleChange('note', $event, report.id)"
                @keyup.enter.native="handleSave(report.id, 'note')"
              ></el-input>
            </div>
          </div>
        </div>
        <div class="wrapper page">
          <el-pagination
            :total="total"
            :current-page="query.page"
            :page-size="30"
            background
            layout="prev, pager, next"
            @current-change="handleCurrentChange"
          ></el-pagination>
        </div>
      </div>
      <album-and-swipe-picker
        v-bind="albumParamsForWithholding"
        @cancel="handleCancelSelectWithholdingAlbumDialog"
        @confirm="handleConfirmSelectWithholdingAlbumDialog($event)"
      ></album-and-swipe-picker>
    </div>
  </page-wrapper>
</template>

<script>
import DatePicker from "@/lib/components/DatePicker";
import { DateTime } from "luxon";
import PageWrapper from "@/lib/components/PageWrapper";
import { baanRueJaiClient } from "@/dependencies";
import ScButton from "@/lib/components/ScButton";
import { Select, Option, Input, Pagination } from "element-ui";
import { convertCsvToExcelBuffer, getKeyByUrlPath } from "@/lib/util";
import { actionBillingMapping } from "@/lib/mapper";
import AlbumAndSwipePicker from "@/lib/components/AlbumAndSwipePicker";

export default {
  components: {
    PageWrapper,
    DatePicker,
    ScButton,
    "el-select": Select,
    "el-option": Option,
    "el-input": Input,
    "el-pagination": Pagination,
    "album-and-swipe-picker": AlbumAndSwipePicker,
  },
  data() {
    const filterStatus = [
      { label: "ทั้งหมด", value: "" },
      { label: "Active", value: "active" },
      { label: "Cancelled", value: "cancelled" },
      { label: "Unsubscribed", value: "unsubscribed" },
      { label: "Expired", value: "expired" },
    ];

    const filterReceiptType = [
      { label: "ทั้งหมด", value: "" },
      { label: "บุคคลธรรมดา", value: "INDIVIDUAL" },
      { label: "นิติบุคคล", value: "CORPORATE" },
    ];

    return {
      reports: [],
      dateRange: [],
      filterStatus,
      query: [],
      filterReceiptType,
      total: 0,
      isClearDisabled: true,
      isSearchDisabled: true,
      isClickDownloadAsCSVDisabled: true,
      isClickDownloadWithIncentiveAsCSVDisabled: true,
      isPaginationDisable: true,
      isDropdownDisabled: true,
      isRouterLinkDisabled: true,
      albumParamsForWithholding: undefined,
    };
  },
  computed: {
    billingReportPresenters() {
      const header = {
        signed_at: "วันที่เอกสาร",
        document_number: "เลขที่ใบเสร็จ",
        billing_address: "ข้อมูลใบกำกับภาษี",
        mobile_number: "หมายเลขโทรศัพท์",
        branch_number: "นิติบุคคล",
        action: "Action",
        action_status: "สถานะ",
        note: "หมายเหตุ",
      };

      return [header, ...this.reports];
    },
    queryParams() {
      const { from, to, status, receiptType, page, documentNumber } =
        this.$route.query;

      return {
        from,
        to,
        status,
        receiptType,
        page: (page && +page) || 1,
        documentNumber,
      };
    },
    totalCount() {
      if (!this.total || this.total === 0) {
        return "ไม่พบข้อมูล";
      }
      const limit = 30;
      const total = this.total.toLocaleString();
      const currentTotalItem = this.reports.length;

      const isLastPage = limit > currentTotalItem;
      const currentPage = this.query.page || 1;
      const end = (isLastPage ? total : currentPage * limit).toLocaleString();
      const start = (
        (isLastPage ? limit * currentPage - limit : end - limit) + 1
      ).toLocaleString();

      return `แสดงผล ${start} - ${end} จากทั้งหมด ${total}`;
    },
    selectWithholdingImageAlbumDialog() {
      return {
        isShow: false,
        title: "Withholding Tax Album (optional)",
        album: undefined,
        canEdit: true,
        imageName: "withholding_tax",
        titleForChooseImage: "เลือกรูป หัก ณ ที่จ่าย",
        pathImage: "withholding_tax",
        isNotMakePublicImage: true,
      };
    },
    actionBillingFilterMapping() {
      return [{ label: "ทั้งหมด", value: "" }].concat(actionBillingMapping)
    }
  },
  watch: {
    "$route.query": function onRouteQueryChange() {
      this.setupForm();
      this.setupData();

      this.$refs.pageEl.$el.querySelector(".page-content").scrollTop = 0;
    },
    // dateRange(newValue, oldValue) {
    //   if (!newValue) {
    //     return;
    //   }

    //   const [from, to] = newValue;

    //   if (to.minus({ days: 93 }) > from) {
    //     this.$message.error("ไม่สามารถเลือกเกิน 3 เดือนได้");
    //     this.dateRange = oldValue;
    //   }
    // },
  },
  created() {
    this.setup();
  },
  methods: {
    setup() {
      this.setupForm();
      this.setupData();
    },
    setupForm() {
      const { from, to } = this.$route.query;

      this.query = {
        status: this.$route.query.status || null,
        receiptType: this.$route.query.receiptType || null,
        page:
          this.$route.query.page && this.$route.query.page !== ""
            ? +this.$route.query.page || 1
            : 1,
        documentNumber: this.$route.query.documentNumber || null,
      };

      if (from && to) {
        this.dateRange = [DateTime.fromISO(from), DateTime.fromISO(to)];
      } else if (!this.query.documentNumber) {
        const formDateTime = DateTime.utc().setZone("utc+7").startOf("day");
        const toDateTime = formDateTime.endOf("day");

        this.dateRange = [
          DateTime.fromISO(formDateTime),
          DateTime.fromISO(toDateTime),
        ];
      }
    },
    setupData() {
      this.setupReports();
    },
    async setupReports() {
      const result = await this.fetchAccountingReports();

      this.reports = result.reports.map((report) => {
        const actionTH = actionBillingMapping.find(
          (act) => act.value === report.action
        );

        const withholdingImagesLength = report.withholding_images
          ? report.withholding_images.length || 0
          : 0;

        return {
          ...report,
          billing_address: report.billing_address.replace(/\n/g, "<br>"),
          action_th: actionTH ? actionTH.label : undefined,
          album: {
            ...this.selectWithholdingImageAlbumDialog,
            album: report.withholding_images,
          },
          albunText: `แนบเอกสารแล้ว ${withholdingImagesLength} รูป`,
        };
      });

      this.total = result.total;

      this.isAbleButton();
    },
    async fetchAccountingReports() {
      try {
        const response = await baanRueJaiClient.admin.subscriptions.getAccountingReports(
          this.queryParams
        );
        return response.data.result;
      } catch (error) {
        const message = error.response
          ? error.response.data.message
          : error.message;

        this.$message.error(`ไม่สามารถดู Report ได้เนื่องจาก ${message}`);

        this.isAbleButton();

        return { reports: [], total: 0 };
      }
    },
    handleClickSearch() {
      const from =
        this.dateRange && this.dateRange.length > 1
          ? this.dateRange[0].toISO()
          : undefined;

      const to =
        this.dateRange && this.dateRange.length > 1
          ? this.dateRange[1].toISO()
          : undefined;

      const query = {
        from,
        to,
        status: this.query.status,
        receiptType: this.query.receiptType,
        page: 1,
        documentNumber: this.query.documentNumber,
      };

      this.$router.push({ query });
      this.isDisableButton();
    },
    cloneWithEmpty(obj) {
      return Object.keys(obj).reduce((all, key) => ({ ...all, [key]: "" }), {});
    },
    async handleClickDownloadAsCSV(event) {
      event.preventDefault();
      this.isDisableButton();
      const response = await baanRueJaiClient.admin.subscriptions.getReports({
        ...this.queryParams,
        format: "csv",
      });

      const excelBuffer = convertCsvToExcelBuffer(response.data);

      const blob = new Blob([excelBuffer]);
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `subscription-reports.xlsx`;
      link.click();
      this.isAbleButton();
    },
    handleClickClear() {
      this.query.status = "";
      this.query.receiptType = "";
      this.query.documentNumber = "";
      this.query.page = 1;
      this.query.from = "";
      this.query.to = "";

      this.dateRange = null;
    },
    handleCurrentChange(page) {
      this.isDisableButton();
      const query = { ...this.$route.query, page };

      this.$router.push({ query });
    },
    isDisableButton() {
      this.isSearchDisabled = false;
      this.isClearDisabled = false;
      this.isClickDownloadAsCSVDisabled = false;
      this.isClickDownloadWithIncentiveAsCSVDisabled = false;
      this.isPaginationDisable = false;
      this.isDropdownDisabled = false;
      this.isRouterLinkDisabled = false;
    },
    isAbleButton() {
      this.isSearchDisabled = true;
      this.isClearDisabled = true;
      this.isClickDownloadAsCSVDisabled = true;
      this.isClickDownloadWithIncentiveAsCSVDisabled = true;
      this.isPaginationDisable = true;
      this.isDropdownDisabled = true;
      this.isRouterLinkDisabled = true;
    },
    handleChange(key, value, receiptId) {
      this.reports = this.reports.map((report) => {
        if (receiptId !== report.id) return { ...report };

        return {
          ...report,
          [key]: value,
        };
      });
    },
    async handleSave(receiptId, key) {
      const billing = this.reports.find((report) => report.id === receiptId);

      if (!billing) {
        this.$message.error("ไม่สามารถบันทึกข้อมูลได้");
        return;
      }

      let nextAction;
      if (key === "action") {
        try {
          await this.$confirm("ยืนยันการเปลี่ยน action", "Warning", {
            confirmButtonText: "OK",
            cancelButtonText: "Cancel",
            type: "warning",
          });
        } catch (e) {
          return;
        }

        const indexOfActionBilling = actionBillingMapping.findIndex(
          (object) => object.value === billing.action
        );

        const nextBillingAction =
          actionBillingMapping[indexOfActionBilling + 1];

        if (!nextBillingAction) {
          this.$message.error("ไม่สามารถบันทึกข้อมูลได้ เนื่องจากหา action ต่อไปไม่พบ");
          return;
        }

        nextAction = nextBillingAction.value;
      }

      const note = billing.note || null;

      const params = {
        note,
        next_action: nextAction,
      };

      await this.updateBillingAction(receiptId, params);
    },
    async updateBillingAction(receiptId, params) {
      try {
        await baanRueJaiClient.admin.subscriptions.receipts.postBillingAction(
          receiptId,
          params
        );

        await this.setup();
      } catch (error) {
        this.$message.error("ไม่สามารถบันทึกข้อมูลได้");
      }
    },
    handleShowImageEditer(report) {
      this.albumParamsForWithholding = {
        ...report.album,
        isShow: true,
        remark: {
          firstRemark: `ชื่อ: ${report.name}`,
          secondRemark: `เลขที่ใบเสร็จ: ${report.document_number}`,
        },
        invoiceId: report.invoice_id,
      };
    },
    handleCancelSelectWithholdingAlbumDialog() {
      this.albumParamsForWithholding = undefined;

      this.reports = this.reports.map((report) => ({
        ...report,
        album: {
          ...this.selectWithholdingImageAlbumDialog,
          album: report.withholding_images,
        },
      }));
    },
    async handleConfirmSelectWithholdingAlbumDialog(album) {
      const withholdingTaxKeyAlbum =
        album && album.allImageToAlbum && album.allImageToAlbum.length > 0
          ? getKeyByUrlPath(album.allImageToAlbum)
          : undefined;

      if (!this.albumParamsForWithholding.invoiceId) {
        this.$message.error("ไม่สามารถบันทึกรูปใบกำกับภาษี หรือ ไม่มีข้อมูลใบกำกับภาษี");
        return;
      }

      await this.updateWithholdingTaxAlbum(withholdingTaxKeyAlbum);

      this.setup();
    },
    async updateWithholdingTaxAlbum(withholdingTaxKeyAlbum) {
      try {
        await baanRueJaiClient.admin.subscriptions.receipts.putWithholdingTaxImageKeys(
          this.albumParamsForWithholding.invoiceId,
          {
            withholding_tax_keys:
              withholdingTaxKeyAlbum && withholdingTaxKeyAlbum.length > 0
                ? withholdingTaxKeyAlbum
                : null,
          }
        );

        this.handleCancelSelectWithholdingAlbumDialog();
        this.$message.success("บันทึกเรียบร้อย");
      } catch (e) {
        this.$message.error("ไม่สามารถบันทึก รูปใบกำกับได้");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";

.report-container {
  padding: 20px 0;
  width: 100%;
}

.report-billing-row {
  display: grid;
  grid-template-columns: 200px 200px 450px 150px 170px 200px 200px 500px;
}

.badge {
  display: inline-block;
  font-size: 0.9rem;
  padding: 15px 15px;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    border-bottom: 0;
    color: #444;
  }

  &.CONTACT_USER {
    background-color: $danger-color;
  }

  &.NEED_DOCUMENT_BILLING {
    background-color: $warning-color;
  }

  &.WAITING_CONFIRM {
    background-color: $secondary-color;
  }

  &.DONE {
    background-color: $success-color;
    cursor: default;

    &:hover {
      border-bottom: 0;
      color: white;
    }
  }
}

.action {
  display: inline-block;
  padding: 10px 15px;
  border-radius: 4px;

  &.CONTACT_USER {
    color: $danger-color;
  }

  &.NEED_DOCUMENT_BILLING {
    color: $warning-color;
  }

  &.WAITING_CONFIRM {
    color: $secondary-color;
  }

  &.DONE {
    color: $success-color;
  }
}

.page-container {
  padding: 15px;
}

.report-cell {
  font-family: sans-serif;
  padding: 1rem 0.5rem;
}

.report-billing-row:first-child {
  position: sticky;
  top: 0;
  z-index: 1;

  .report-cell {
    border-top: 1px solid #444;
    font-weight: bold;
    background: #ddd;
  }
}

.report-cell {
  a {
    text-decoration: underline;
  }

  border-bottom: 1px solid #ccc;

  &:empty::before {
    content: "N/A";
    color: #d8d8d8;
  }
}

.space {
  padding-left: 0.8em;
}

.page {
  padding-top: 15px;
  text-align: right;
}

.top-page {
  text-align: right;
  padding-bottom: 10px;
}

input[type="checkbox"] {
  transform: scale(1.5);
}
</style>
